import React, { useEffect, useState, useRef } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

interface TermoConteudo {
  id: number;
  titulo: string;
  versao: number;
  conteudo: string;
}

const ModalLgpd = () => {
  const auth = useAuth();
  
  // Extrair com segurança
  const isAutenticado = auth?.isAutenticado ?? false;
  const lgpdPendente = auth?.lgpdPendente ?? null;
  const registrarConsentimentoLgpd = auth?.registrarConsentimentoLgpd;
  const logout = auth?.logout;

  const [termo, setTermo] = useState<TermoConteudo | null>(null);
  const [aceitando, setAceitando] = useState(false);
  const [lido, setLido] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Termo padrão de segurança (fallback)
  const fallback = {
    id: lgpdPendente?.id || 1,
    titulo: 'Termo de Privacidade e Uso — LGPD',
    versao: 1,
    conteudo: 'Este sistema processa dados pessoais de acordo com a Lei Geral de Proteção de Dados (13.709/2018).\n\n' +
              'Ao clicar em "Aceitar", você confirma estar ciente do tratamento de dados necessário para a gestão da creche, ' +
              'incluindo dados cadastrais de responsáveis, crianças e registros pedagógicos.\n\n' +
              'Os dados são mantidos em ambiente seguro e utilizados exclusivamente para as finalidades de gestão e educação.',
  };

  // Busca o status do termo com timeout de segurança
  useEffect(() => {
    if (!lgpdPendente || !isAutenticado) return;

    // Timeout de 2 segundos para não travar o usuário
    const timer = setTimeout(() => {
      if (!termo) {
        setTermo(fallback);
      }
    }, 2000);

    api.get('/lgpd/primeiro-acesso/status')
      .then((res) => {
        if (res.data?.termo) {
          setTermo(res.data.termo);
        } else {
          // Se o backend retornou OK mas sem termo, usa o fallback
          setTermo(fallback);
        }
      })
      .catch((err) => {
        console.warn('Erro ao carregar termo do backend, usando fallback:', err);
        setTermo(fallback);
      });

    return () => clearTimeout(timer);
  }, [lgpdPendente, isAutenticado]);

  // Habilitar botão após leitura/scroll ou se o conteúdo for curto
  useEffect(() => {
    if (termo && scrollRef.current) {
      const el = scrollRef.current;
      // Pequeno delay para garantir que o scrollHeight foi calculado
      setTimeout(() => {
        if (el.scrollHeight <= el.clientHeight) {
          setLido(true);
        }
      }, 50);
    }
  }, [termo]);

  if (!lgpdPendente || !isAutenticado) return null;

  const handleAceitar = async () => {
    if (!lgpdPendente || !registrarConsentimentoLgpd) return;
    setAceitando(true);
    try {
      await registrarConsentimentoLgpd(lgpdPendente.id);
    } catch (err) {
      console.error('Falha ao registrar aceite:', err);
      // Mesmo com erro de rede ao registrar, vamos fechar para não travar o usuário (opcional dependendo da criticidade)
    } finally {
      setAceitando(false);
    }
  };

  const textoConteudo = termo?.conteudo || '';

  return (
    <div className="modal-overlay" style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,.65)',
      zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div className="modal-box" style={{
        background: '#fff', borderRadius: 12, maxWidth: 680, width: '95%',
        padding: '32px 28px', display: 'flex', flexDirection: 'column', gap: 18,
        maxHeight: '90dvh',
      }}>
        <h2 style={{ margin: 0, fontSize: '1.2rem' }}>
          📋 Termo de Privacidade — LGPD
        </h2>

        <p style={{ margin: 0, color: '#666', fontSize: '.9rem' }}>
          {termo?.titulo && <strong>{termo.titulo}{termo?.versao ? ` (v${termo.versao})` : ''}</strong>}
          {' '}— Para continuar, leia e aceite os termos abaixo.
        </p>

        <div
          ref={scrollRef}
          onScroll={(e) => {
            const el = e.currentTarget;
            if (!lido && el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
              setLido(true);
            }
          }}
          style={{
            border: '1px solid #ddd', borderRadius: 8, padding: '16px',
            overflowY: 'auto', flex: 1, maxHeight: 340, fontSize: '.9rem',
            lineHeight: 1.6, whiteSpace: 'pre-wrap', background: '#f9f9f9',
          }}
        >
          {textoConteudo || 'Iniciando leitura…'}
        </div>

        {!lido && textoConteudo && (
          <p style={{ margin: 0, color: '#999', fontSize: '.82rem' }}>
            Role até o final para habilitar o botão de aceite.
          </p>
        )}

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', alignItems: 'center' }}>
          <button
            onClick={logout}
            style={{
              padding: '9px 20px', borderRadius: 8, border: '1px solid #ddd',
              background: '#fff', cursor: 'pointer', fontSize: '.9rem',
            }}
          >
            Recusar e sair
          </button>
          <button
            onClick={handleAceitar}
            disabled={!lido || aceitando}
            style={{
              padding: '9px 20px', borderRadius: 8, border: 'none',
              background: lido ? '#2563eb' : '#94a3b8',
              color: '#fff', cursor: lido ? 'pointer' : 'not-allowed',
              fontSize: '.9rem', fontWeight: 600,
            }}
          >
            {aceitando ? 'Registrando…' : 'Aceitar e Continuar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalLgpd;



